// SPDX-License-Identifier: FSL-1.1-Apache-2.0
/**
 * @module AnalyticsProviderInterface
 * @package @geenius/analytics
 * @description Defines the provider contract and base implementation used by
 * all shared analytics provider integrations. These types keep the tracker
 * runtime provider-agnostic while still supporting provider-specific setup.
 */

import {
  AdapterUnsupportedError,
  AnalyticsConfigurationError,
} from "../errors";
import type {
  AnalyticsConfig,
  AnalyticsEvent,
  PageView,
  ProviderName,
  UserTraits,
} from "../types";

/**
 * Re-exports shared user trait metadata for provider integrations.
 */
export type { UserTraits };

/**
 * Provider name token supported by the shared analytics package.
 */
export type AnalyticsProviderName = ProviderName;

/**
 * Provider-specific configuration required to initialize an analytics client.
 *
 * @property provider Provider identifier used to select the integration.
 * @property apiKey Provider API key, measurement ID, or equivalent identifier.
 * @property host Optional host override for self-hosted analytics backends.
 */
export interface AnalyticsProviderConfig extends AnalyticsConfig {
  provider: AnalyticsProviderName;
  apiKey: string;
  host?: string;
}

/**
 * Contract implemented by all analytics providers used by the shared tracker.
 *
 * @property name Stable provider identifier exposed by the implementation.
 */
export interface AnalyticsProvider {
  readonly name: AnalyticsProviderName;
  init(config: AnalyticsProviderConfig): void;
  trackEvent(event: AnalyticsEvent): Promise<void>;
  trackPageView(pageView: PageView): Promise<void>;
  identify(userId: string, traits?: UserTraits): Promise<void>;
  setUserProperties(userId: string, traits: UserTraits): Promise<void>;
  flush(): Promise<void>;
  reset(): Promise<void>;
  shutdown(): Promise<void>;
  purgeExpiredData?(cutoffTimestamp: number): Promise<void>;
  deleteUserData?(userId: string): Promise<void>;
}

/**
 * Base class that stores configuration and supplies common provider defaults.
 */
export abstract class BaseAnalyticsProvider implements AnalyticsProvider {
  abstract readonly name: AnalyticsProviderName;
  protected config: AnalyticsProviderConfig | null = null;

  /**
   * Stores the provider configuration for later event and identity calls.
   *
   * @param config Provider configuration for the current integration instance.
   * @returns Nothing.
   */
  init(config: AnalyticsProviderConfig): void {
    this.config = config;
  }

  /**
   * Returns the provider configuration or throws when initialization was skipped.
   *
   * @returns The previously stored provider configuration.
   * @throws {AnalyticsConfigurationError} Thrown when the provider was used before initialization.
   */
  protected getRequiredConfig(): AnalyticsProviderConfig {
    if (!this.config) {
      throw new AnalyticsConfigurationError({
        message: `${this.name} provider was used before init() completed.`,
        context: { provider: this.name },
      });
    }

    return this.config;
  }

  abstract trackEvent(event: AnalyticsEvent): Promise<void>;
  abstract trackPageView(pageView: PageView): Promise<void>;

  /**
   * Identifies a user with optional trait metadata. Providers may override this
   * no-op default when they support identity calls.
   *
   * @param _userId User identifier associated with the current analytics subject.
   * @param _traits Optional user traits supplied to the provider.
   * @returns Nothing.
   */
  async identify(_userId: string, _traits?: UserTraits): Promise<void> {
    // No-op by default
  }

  /**
   * Updates provider-side user properties. Providers may override this no-op
   * default when they support profile traits separate from identify calls.
   *
   * @param _userId User identifier whose profile should be updated.
   * @param _traits User traits to attach to the provider profile.
   * @returns Nothing.
   */
  async setUserProperties(_userId: string, _traits: UserTraits): Promise<void> {
    // No-op by default
  }

  /**
   * Flushes any provider-owned queue. Providers may override this no-op default
   * when their SDK buffers analytics payloads.
   *
   * @returns Nothing.
   */
  async flush(): Promise<void> {
    // No-op by default
  }

  /**
   * Clears provider session state. Providers may override this no-op default
   * when they support resetting their local client state.
   *
   * @returns Nothing.
   */
  async reset(): Promise<void> {
    // No-op by default
  }

  /**
   * Shuts down provider-owned resources after the shared tracker has flushed.
   *
   * @returns Nothing.
   */
  async shutdown(): Promise<void> {
    // No-op by default
  }

  /**
   * Reports that durable provider retention purge is not available.
   *
   * Browser analytics SDKs generally cannot delete historical provider records
   * from the client. Providers that support a real server-side purge should
   * override this method instead of inheriting the typed unsupported error.
   *
   * @param cutoffTimestamp Oldest retained record timestamp in milliseconds.
   * @returns Nothing when an overriding provider supports the purge.
   * @throws {AdapterUnsupportedError} Thrown when the provider cannot safely purge durable data.
   */
  async purgeExpiredData(cutoffTimestamp: number): Promise<void> {
    throw new AdapterUnsupportedError({
      message: `${this.name} provider does not support client-side retention purge.`,
      context: {
        provider: this.name,
        operation: "purgeExpiredData",
        cutoffTimestamp,
      },
    });
  }

  /**
   * Reports that durable provider user deletion is not available.
   *
   * Browser analytics SDKs can reset local identity state, but durable
   * provider-side deletion requires a server-authorized data deletion endpoint.
   * Providers that implement such an endpoint should override this method.
   *
   * @param userId User identifier to delete from the provider.
   * @returns Nothing when an overriding provider supports deletion.
   * @throws {AdapterUnsupportedError} Thrown when the provider cannot safely delete durable data.
   */
  async deleteUserData(userId: string): Promise<void> {
    throw new AdapterUnsupportedError({
      message: `${this.name} provider does not support client-side user deletion.`,
      context: {
        provider: this.name,
        operation: "deleteUserData",
        userId,
      },
    });
  }
}
